import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { OWN_SESSION_REQUEST } from '@interceptors/own-session.context';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { IS_STOREFRONT_REQUEST } from '@prosell/feature-customer-auth/data-access/storefront-request.context';
import { MobileAuthService } from './mobile-auth.service';

const IS_MOBILE_RETRY = new HttpContextToken<boolean>(() => false);

export const mobileApiInterceptor: HttpInterceptorFn = (req, next) => {
  const mobileAuth = inject(MobileAuthService);
  const router = inject(Router);

  const accessToken = mobileAuth.getAccessToken();
  // M04/32 revisión de seguridad: `/api/prosell/` sin más califica CUALQUIER llamada de la tienda
  // también (comparten `/api/prosell/sales/**` con el móvil) — sin esta exclusión, un visitante
  // con sesión móvil Y de tienda a la vez en el mismo navegador vería sus peticiones de tienda
  // secuestradas con el Bearer móvil: el backend ahora valida TODO bearer contra la superficie
  // MOBILE, así que autenticaría igual, pero como la identidad móvil, no la de tienda — exactamente
  // la mezcla de sesiones que M04/32 existe para evitar.
  const isStorefrontRequest = req.context.get(IS_STOREFRONT_REQUEST);
  const isMobileApi = !isStorefrontRequest
    && (req.url.includes('/api/prosell/mobile/') || req.url.includes('/api/prosell/'));
  const isMobileAuthPath = req.url.includes('/api/prosell/mobile/customer-auth/');

  if (!accessToken || !isMobileApi) {
    return next(req);
  }

  const authedReq = isMobileAuthPath
    ? req.clone({
        withCredentials: false,
        context: req.context.set(OWN_SESSION_REQUEST, true),
      })
    : req.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
        withCredentials: false,
        context: req.context.set(OWN_SESSION_REQUEST, true),
      });

  return next(authedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const isRetry = req.context.get(IS_MOBILE_RETRY);
      if (error.status !== 401 || isRetry || isMobileAuthPath) {
        return throwError(() => error);
      }

      return mobileAuth.refresh().pipe(
        switchMap(res => {
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${res.accessToken}` },
            withCredentials: false,
            context: req.context.set(OWN_SESSION_REQUEST, true).set(IS_MOBILE_RETRY, true),
          });
          return next(retryReq);
        }),
        catchError(() => {
          mobileAuth.logout();
          void router.navigate(['/m/login']);
          return throwError(() => error);
        })
      );
    })
  );
};
